"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import { Card, Heading, useThemeContext } from "@radix-ui/themes";
import { WhitelabelConfig, EventData } from "@types";
import { formatPrice, joinClassNames } from "@utils/helpers";
import {
  EventConfigOverrides,
  PromoterConfigOverrides,
  WebsiteConfigOverrides,
} from "@utils/flags";
import { EventListMode } from "@features/eventListMode";
import EventInfoAccordion from "@features/eventPage/components/eventInfoAccordion";
import calculatePriceRange from "@features/eventPage/utils/calculatePriceRange";
import EventPastError from "@features/eventPage/components/eventPastError";
import EventSubDescription from "@features/eventPage/components/eventSubDescription";
import EventDescription from "@features/eventPage/components/eventDescription";
import EventInfo from "@features/eventPage/components/eventInfo";
import { EventMode } from "@utils/enums";
import EventCalendarMode from "@features/eventCalendarMode";
import LandingPageLinkV2 from "../../../../components/links/landingPageLinkV2";
import EventTicketButton from "@features/eventPage/components/eventTicketButton";
import StickyEventTicketButton from "@features/eventPage/components/stickyEventTicketButton";
import EventDisabledError from "@features/eventPage/components/eventDisabledError";
import TicketGate from "@features/queue/ticketGate";
import useDomainBasedTranslations from "@utils/useDomainBasedTranslations";
import useQueue from "@features/eventPage/utils/useQueue";

interface Props {
  event: EventData;
  edgeConfig?: WhitelabelConfig;
  eventConfigOverrides?: EventConfigOverrides;
  promoterConfigOverrides?: PromoterConfigOverrides;
  websiteConfigOverrides?: WebsiteConfigOverrides;
  /** Normalised event path (after /events/) used to look up queue config. */
  queueSlug?: string;
}

const EventDetailsWL = ({
  event,
  eventConfigOverrides,
  promoterConfigOverrides,
  websiteConfigOverrides,
  edgeConfig,
  queueSlug,
}: Props) => {
  const theme = useThemeContext();
  const appearance = theme.appearance;
  const t = useDomainBasedTranslations("EventPage");
  const locale = useLocale();
  const searchParams = useSearchParams();

  const { queueState, setQueueState, queueStatus, admittedRef, opensAt } =
    useQueue(queueSlug);

  const mainButtonRef = useRef<HTMLDivElement>(null);
  const storageKey = `event-view-${event.slug}`;

  const configDefault = edgeConfig?.defaultShowDescription ?? false;

  // Initialise to the config default only. This value is known on the server,
  // so the server render and the first client render agree (no hydration
  // mismatch). sessionStorage is client-only and is read in the effect below.
  const [showEventDescription, setShowEventDescription] =
    useState<boolean>(configDefault);

  // After mount, resolve the view in this order of precedence:
  // 1. A `view` query param (`info`/`tickets`) so external links can target a
  //    specific view. This is read client-side only, so it doesn't rely on a
  //    Link navigation (which previously broke on whitelabel subdomains).
  // 2. The stored preference from sessionStorage.
  // 3. The config default.
  // Storage semantics are consistent regardless of config:
  // "info" = description view, "tickets" = tickets view.
  useEffect(() => {
    const viewParam = searchParams.get("view");
    if (viewParam === "info" || viewParam === "tickets") {
      sessionStorage.setItem(storageKey, viewParam);
      setShowEventDescription(viewParam === "info");
      return;
    }

    const stored = sessionStorage.getItem(storageKey);
    if (stored === "info") {
      setShowEventDescription(true);
    } else if (stored === "tickets") {
      setShowEventDescription(false);
    } else {
      setShowEventDescription(configDefault);
    }
  }, [storageKey, configDefault, searchParams]);

  const handleToggleView = (newValue: boolean) => {
    sessionStorage.setItem(storageKey, newValue ? "info" : "tickets");
    setShowEventDescription(newValue);
  };

  const hasPaymentInfo = event.payment.length > 0;

  const getPriceRange = useCallback(
    (ids?: { pricesectionIds?: number[]; groupedPricesectionIds?: number[] }) =>
      calculatePriceRange(event.pricing.priceschemes ?? [], ids),
    [event.pricing.priceschemes]
  );

  const priceRange = getPriceRange();

  const priceRangeText =
    event.info.pricerange ||
    `${
      priceRange.min !== priceRange.max
        ? formatPrice(locale, event.currency.code, priceRange.min) + " - "
        : ""
    }${formatPrice(locale, event.currency.code, priceRange.max)} + ${t(
      "bookingfee"
    )}`;

  const disabledByPromoter =
    promoterConfigOverrides?.disableAllEvents === true ||
    (edgeConfig?.eventPageSales?.disabledByOrganisation &&
      edgeConfig?.eventPageSales.disabledByOrganisation.find(
        (uid) => uid.toLowerCase() === event.organisation.uid.toLowerCase()
      ));

  const disabledByEvent =
    eventConfigOverrides?.disableEvent === true ||
    (edgeConfig?.eventPageSales?.disabledByEvent &&
      edgeConfig?.eventPageSales.disabledByEvent.find(
        (uid) => uid.toLowerCase() === event.uid.toLowerCase()
      ));

  const disabledByWebsite = websiteConfigOverrides?.disableAllEvents === true;

  const manuallyDisabled =
    disabledByPromoter || disabledByEvent || disabledByWebsite;

  const filtered_discounts = event.discount?.filter(
    (discount) =>
      discount.active &&
      (discount.combo
        ? discount.combo.active && !discount.combo.hidden
        : true) &&
      (discount.type.name === "Combo" ? !!discount.combo : true)
  );

  const hasDiscountInfo = filtered_discounts && filtered_discounts.length > 0;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 w-full gap-6">
        {event.status !== "past" ? (
          <div className="md:hidden" ref={mainButtonRef}>
            <EventTicketButton
              showEventDescription={showEventDescription}
              onClick={(e) => {
                e.preventDefault();
                handleToggleView(!showEventDescription);
              }}
            />
          </div>
        ) : (
          <></>
        )}
        <div className="col-span-full md:col-span-2">
          {!showEventDescription &&
          !manuallyDisabled &&
          event.status !== "past" ? (
            <Card size="3" className="max-sm:pb-0">
              <div className="lg:max-w-lg">
                <Heading weight="bold" size={{ initial: "7", sm: "8" }}>
                  {t("gettickets")}
                </Heading>
              </div>
              {event.subdescription ? (
                <EventSubDescription subdescription={event.subdescription} />
              ) : (
                <></>
              )}
              <TicketGate
                eventName={event.name}
                queueState={queueState}
                setQueueState={setQueueState}
                queueStatus={queueStatus}
                opensAt={opensAt}
                admittedRef={admittedRef}
              >
                {event.settings.showingdisplaytype === EventMode.LIST ? (
                  <EventListMode
                    event={event}
                    getPriceRange={getPriceRange}
                    eventConfigOverrides={eventConfigOverrides}
                  />
                ) : (
                  <EventCalendarMode
                    event={event}
                    getPriceRange={getPriceRange}
                  />
                )}
              </TicketGate>
            </Card>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col gap-6 md:gap-4"
            >
              {event.status === "past" ||
              manuallyDisabled ||
              hasPaymentInfo ||
              hasDiscountInfo ? (
                <Card size="3">
                  {event.status === "past" ? (
                    <EventPastError />
                  ) : manuallyDisabled ? (
                    <EventDisabledError
                      reason={
                        disabledByWebsite
                          ? "website"
                          : disabledByPromoter
                          ? "promoter"
                          : "event"
                      }
                    />
                  ) : (
                    <></>
                  )}
                  <EventInfoAccordion
                    hasPaymentInfo={hasPaymentInfo}
                    priceschemes={event.pricing.priceschemes}
                    discount={event.discount}
                    currency={event.currency.code}
                  />
                </Card>
              ) : (
                <></>
              )}
              <Card size="3">
                <div className="w-full">
                  <Heading weight="bold" size={{ initial: "7", sm: "8" }}>
                    {t("description")}
                  </Heading>
                  {event.subdescription ? (
                    <EventSubDescription
                      subdescription={event.subdescription}
                    />
                  ) : (
                    <></>
                  )}
                </div>
                <EventDescription
                  description={event.description}
                  appearance={appearance}
                />
              </Card>
            </motion.div>
          )}
          {event.landingpages && event.landingpages.length > 0 ? (
            <Card
              size="3"
              mt={{ initial: "6", md: "4" }}
              className="flex flex-col gap-4"
            >
              <Heading className="text-lg md:text-xl font-bold">
                {t("exploremore", {
                  landingpages: event.landingpages
                    .map((v, i) => {
                      return event.landingpages.length === 1
                        ? v.title
                        : event.landingpages.length === i + 2
                        ? `${v.title} & `
                        : event.landingpages.length > i + 1
                        ? `${v.title}, `
                        : v.title;
                    })
                    .join(""),
                })}
              </Heading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {event.landingpages.map((landingpage) => (
                  <LandingPageLinkV2
                    key={landingpage.id}
                    slug={landingpage.url}
                    image={landingpage.images.header}
                    name={landingpage.title}
                    showNameOnMobile={event.landingpages.length > 1}
                  />
                ))}
              </div>
            </Card>
          ) : (
            <></>
          )}
        </div>
        <div
          className={joinClassNames(
            "col-span-full md:col-span-1",
            showEventDescription ? "max-md:row-start-2" : ""
          )}
        >
          <div className="md:pb-12 md:sticky md:top-36 md:col-start-2 md:row-span-2 md:row-start-1 ">
            <EventInfo
              event={event}
              priceRangeText={priceRangeText}
              showEventDescription={showEventDescription}
              onToggleView={() => handleToggleView(!showEventDescription)}
            />
          </div>
        </div>
      </div>
      {!manuallyDisabled && event.status !== "past" && (
        <StickyEventTicketButton
          mainButtonRef={mainButtonRef}
          showEventDescription={showEventDescription}
          onClick={(e) => {
            e.preventDefault();
            handleToggleView(!showEventDescription);
          }}
        />
      )}
    </>
  );
};

export default EventDetailsWL;
